import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { WebhookHealthStatus } from "@/hooks/useWebhookErrorDetection";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { getStatusBadgeVariant, STATUS_ICONS } from "@/lib/status-appearance";

// N-12: the shared `destructive` alert variant inherits `text-destructive`, which
// measures 1.84:1 against this alert's dark-mode background -- unreadable. This
// overrides the title and description with a pair that clears 4.5:1 in both themes
// (checked against the light/dark `--background` tokens, not just eyeballed).
const DESTRUCTIVE_TEXT_CLASS = "text-red-800 dark:text-red-300";

interface WebhookErrorAlertProps {
  status: WebhookHealthStatus;
  onRetry?: () => void;
  isRetrying?: boolean;
  onOpenSettings?: () => void;
  className?: string;
  // N-11: the alert used to close with "contact your administrator" no matter who was
  // looking at it -- including the administrator. When the viewer can act, they get a
  // real button; when they cannot, they get OKOS support, not an anonymous reference.
  canReconnect?: boolean;
  reconnectHref?: string;
}

export function WebhookErrorAlert({
  status,
  onRetry,
  isRetrying = false,
  onOpenSettings,
  className = "",
  canReconnect = false,
  reconnectHref,
}: WebhookErrorAlertProps) {
  const { t } = useTranslation("conversations");
  const [showTechnicalDetail, setShowTechnicalDetail] = useState(false);

  // Show the alert if authentication is failing, or there are recent errors, or the
  // measured success rate over the window is genuinely low -- never for "no data yet"
  // (totalCalls === 0), which used to be indistinguishable from "100% healthy".
  const hasMeasurement = (status.totalCalls ?? 0) > 0;
  if (
    status.isHealthy &&
    !status.authenticationFailing &&
    status.recentErrors.length === 0 &&
    (!hasMeasurement || status.successRate > 50)
  ) {
    return null;
  }

  // The source is a boolean (`authenticationFailing`), translated to a status name before
  // asking the shared module (src/lib/status-appearance.ts, entity `webhook_health`) for the
  // appearance. `error` resolves off the module's generic map (destructive/XCircle); `degraded`
  // is this entity's own -- deliberately kept to a variant `Alert` itself supports (`default`
  // only besides `destructive`, see components/ui/alert.tsx).
  // A QR account whose bridge stopped answering is its own alarm: no Meta credential, no rate.
  const bridgeDown = status.bridgeDown === true && !status.authenticationFailing;
  const webhookStatusName = status.authenticationFailing || bridgeDown ? "error" : "degraded";

  const getErrorIcon = () => {
    const Icon = STATUS_ICONS[webhookStatusName] ?? AlertTriangle;
    return <Icon className="h-5 w-5" />;
  };

  const getErrorVariant = (): "default" | "destructive" => {
    return (getStatusBadgeVariant("webhook_health", webhookStatusName) as "default" | "destructive" | undefined) ?? "default";
  };

  // N-08: the title and body used to read like a stack trace ("Webhook Authentication
  // Failed", "your WHATSAPP_APP_SECRET may be incorrect"). This says what the merchant
  // actually lost -- their WhatsApp messages -- and since when.
  const getErrorTitle = () => {
    if (bridgeDown) return t("webhookAlert.bridgeTitle");
    if (status.authenticationFailing) {
      return t("webhookAlert.authTitle");
    }
    if (hasMeasurement && status.successRate < 50) {
      return t("webhookAlert.criticalTitle");
    }
    return t("webhookAlert.issuesTitle");
  };

  const sinceDate = status.lastError?.timestamp ?? status.lastSuccessfulRequest;

  const getErrorDescription = () => {
    if (bridgeDown) return t("webhookAlert.bridgeDescription");
    if (status.authenticationFailing) {
      return sinceDate
        ? t("webhookAlert.authDescriptionSince", { date: format(sinceDate, "d MMM, HH:mm") })
        : t("webhookAlert.authDescription");
    }
    if (hasMeasurement && status.successRate < 50) {
      return t("webhookAlert.criticalDescription", { rate: status.successRate.toFixed(0) });
    }
    return t("webhookAlert.issuesDescription", { rate: status.successRate.toFixed(0) });
  };

  const isDestructive = getErrorVariant() === "destructive";

  return (
    <Alert variant={getErrorVariant()} className={`border-l-4 ${className}`}>
      <div className="flex items-start gap-3">
        {getErrorIcon()}
        <div className="flex-1 space-y-3">
          <div className="space-y-2">
            <AlertTitle className={cn("text-sm font-semibold", isDestructive && DESTRUCTIVE_TEXT_CLASS)}>
              {getErrorTitle()}
            </AlertTitle>
            <AlertDescription className={cn("text-sm leading-relaxed", isDestructive && DESTRUCTIVE_TEXT_CLASS)}>
              {getErrorDescription()}
            </AlertDescription>
          </div>

          {/* Technical detail -- folded away by default (N-08) */}
          {status.lastError && !bridgeDown && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowTechnicalDetail(!showTechnicalDetail)}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                {showTechnicalDetail ? t("webhookAlert.hideTechnicalDetail") : t("webhookAlert.showTechnicalDetail")}
              </button>
              {showTechnicalDetail && (
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">
                    {format(status.lastError.timestamp, "MMM dd, HH:mm:ss")}
                  </span>
                  <div className="text-xs bg-muted/50 p-2 rounded border font-mono">
                    {status.lastError.message}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action -- reconnect if the viewer can, real OKOS support if they cannot (N-11) */}
          {status.authenticationFailing && (
            <div className="flex items-center gap-2 pt-2">
              {canReconnect && reconnectHref ? (
                <Button asChild size="sm">
                  <a href={reconnectHref}>{t("webhookAlert.reconnectAction")}</a>
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("webhookAlert.contactSupport")}{" "}
                  <a href="https://okostec.ai" target="_blank" rel="noreferrer" className="underline">
                    okostec.ai
                  </a>
                </p>
              )}
            </div>
          )}

          {bridgeDown && canReconnect && reconnectHref && (
            <div className="flex items-center gap-2 pt-2">
              <Button asChild size="sm">
                <a href={`${reconnectHref}?tab=webhook-settings`}>{t("webhookAlert.openAccounts")}</a>
              </Button>
            </div>
          )}

          {onOpenSettings && !status.authenticationFailing && !bridgeDown && (
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onOpenSettings}
                className="text-xs"
              >
                {t("webhookAlert.openSettings")}
              </Button>
            </div>
          )}

          {/* Success-rate line -- only when there is something to measure (N-10). Zero
              calls in the window says so instead of a fabricated rate. */}
          {!bridgeDown && (
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
            {hasMeasurement ? (
              <span>{t("webhookAlert.successRateWindow", { rate: status.successRate.toFixed(1) })}</span>
            ) : (
              <span>{t("webhookAlert.noDataInWindow")}</span>
            )}
            {status.lastSuccessfulRequest && (
              <span>
                {t("webhookAlert.lastSuccess", { date: format(status.lastSuccessfulRequest, "MMM dd, HH:mm") })}
              </span>
            )}
          </div>
          )}

          {onRetry && (
            <div className="pt-1">
              <button
                type="button"
                onClick={onRetry}
                disabled={isRetrying}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
              >
                {isRetrying ? t("webhookAlert.retrying") : t("webhookAlert.retryNow")}
              </button>
            </div>
          )}
        </div>
      </div>
    </Alert>
  );
}
